import {
  Form,
  FormAssertive,
  FormInput,
  FormSelect,
  FormSubmit,
  useForm,
  validator,
} from "@/components/form"
import { Button } from "@/components/ui/button"
import { Dialog } from "@/components/ui/dialog"
import { resetAllStoresAndReload } from "@/store"
import { createLanguage } from "@/store/languages/actions"
import { compareCodeToLocale } from "@/store/languages/helpers"
import { all, getByTag } from "locale-codes"
import { match } from "ts-pattern"
import { UseDialogFormProps, UseDialogProps } from "../ui/hooks/useDialog"

/**
 * dictionary src/dictionaries/en/components/dialogs/language-create-dialog.json
 */
const dictionary = createContextMapper("components", "dialogs", "language-create-dialog")

/**
 * LanguageCreateDialog
 */
export const LanguageCreateDialog: React.FC<UseDialogProps<void>> = ({ ...props }) => {
  const { _ } = useDictionary(dictionary())
  return (
    <Dialog {...props} title={_("title")} description={_("secondary")} className="sm:max-w-xl">
      <DialogForm {...props} />
    </Dialog>
  )
}

/**
 * DialogForm
 */
const DialogForm: React.FC<UseDialogFormProps<void>> = ({ onOpenChange }) => {
  const { _ } = useDictionary(dictionary())
  const _form = useFormDictionary()
  const _errors = useErrorsDictionary()

  const { min } = validator
  const form = useForm({
    allowSubmitAttempt: true,
    allowErrorSubmit: true,
    values: {
      name: "",
      code: "",
      locale: "",
    },
    validate: validator({
      name: [min(1, _form("name-required"))],
      code: [min(1, _form("code-required")), field => !!getByTag(field) || _form("code-exists")],
      locale: [
        min(1, _form("locale-required")),
        field => !!getByTag(field) || _form("locale-exists"),
      ],
    }),
    onSubmit: async ({ values: { name, code, locale } }) => {
      if (!form.isValid) return _errors("VALIDATION_FAILURE")
      const payload = { name, code: S.toLowerCase(code), locale }
      return match(await createLanguage(payload))
        .with({ error: false }, () => {
          toast.success(_("success"))
          onOpenChange(false)
        })
        .otherwise(({ code }) =>
          match(code)
            .with("VALIDATION_FAILURE", _errors)
            .with("INVALID_AUTH_SESSION", resetAllStoresAndReload)
            .otherwise(code => void toast.error(_errors(code)))
        )
    },
  })

  const localeOptions = useLocaleOptions(form.values.code)

  return (
    <Form form={form} className="grid gap-6">
      <FormAssertive />
      <FormInput label={_form("name-label")} name="name" placeholder={_form("name-placeholder")} />
      <FormInput label={_form("code-label")} name="code" placeholder={_form("code-placeholder")} />
      <FormSelect
        label={_form("locale-label")}
        name="locale"
        placeholder={_form("locale-placeholder")}
        options={localeOptions}
        disabled={A.isEmpty(localeOptions)}
      />
      <Dialog.Footer className="sm:justify-start">
        <Dialog.Close asChild>
          <Button variant="secondary">{_form("cancel")}</Button>
        </Dialog.Close>
        <FormSubmit>{_form("create")}</FormSubmit>
      </Dialog.Footer>
    </Form>
  )
}

/**
 * useLocaleOptions
 */
export const useLocaleOptions = (current: string) => {
  return React.useMemo(
    () =>
      pipe(
        all,
        A.filter(locale => compareCodeToLocale(current, locale)),
        A.map(({ tag, name, location }) => ({
          label: (
            <>
              <b className="inline-flex w-12">{tag}</b> {name} {location}
            </>
          ),
          value: tag,
        }))
      ),
    [current]
  )
}
